import { useMemo } from 'react';
import { useIntl } from 'gatsby-plugin-intl';
import { useLocation } from '@reach/router';

import type { Breadcrumb } from '../types';

/**
 * useBreadcrumbListSchema - Creates a BreadcrumbList ldJson schema for the SEO component
 * @param {Array} breadcrumbs - An optional list of breadcrumbs from the CMS
 * @param {String} pageTitle - An optional page title to use as the current breadcrumb list item name
 * @returns {Object} BreadcrumbList ldJson schema data
 */
const useBreadcrumbListSchema = (breadcrumbs: Breadcrumb[] = [], pageTitle = '') => ({
  '@context': 'https://schema.org/',
  '@type': 'BreadcrumbList',
  itemListElement: useBreadcrumbItemListSchema(breadcrumbs, pageTitle),
});

/**
 * useBreadcrumbItemListSchema - Creates an array of ListItems for use in the BreadcrumbList ldJson schema
 * @param {Array} breadcrumbs - An optional list of breadcrumbs from the CMS
 * @param {String} pageTitle - An optional page title to use as the current breadcrumb list item name
 * @returns {Array} ItemList ldJson schema data
 */
export const useBreadcrumbItemListSchema = (breadcrumbs : Array<Breadcrumb>, pageTitle = '') => {
  const { locale } = useIntl();
  const { origin, pathname: pathName } = useLocation();

  return useMemo(() => {
    // If the page has breadcrumbs defined in the CMS, use them
    if (breadcrumbs?.length) {
      return breadcrumbs
        .map(breadcrumb => ({
          name: breadcrumb?.linkText?.trim(),
          path: breadcrumb?.linkUrl?.trim(),
        }))
        .filter(({ name, path }) => !!name && !!path)
        .map(({ name, path }, index) => {
          const currentPath = path.replace(/^\/(en|fr)?\/?/i, `/${locale}/`).replace(/\/$/, '');
          return {
            '@type': 'ListItem',
            position: index + 1,
            name,
            item: `${origin}${currentPath}/`,
          };
        });
    }

    // If the page doesn't have breadcrumbs, use the current path to determine breadcrumbs
    return pathName.split('/')
      .filter(pathFragment => !!pathFragment.trim())
      .map((pathFragment, index, pathFragments) => {
        let currentPath = pathFragments.slice(0, index).join('/');
        if (currentPath) currentPath = `/${currentPath}`;
        return {
          '@type': 'ListItem',
          position: index + 1,
          item: `${origin}${currentPath}/${pathFragment}/`,
        };
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((listItem: any, index, listItems) => {
        if (!index) listItem.name = locale === 'fr' ? 'Page d\'accueil' : 'Homepage';
        else if (index + 1 === listItems.length && pageTitle) listItem.name = pageTitle;
        return listItem;
      });
  }, [breadcrumbs, pageTitle, origin, pathName, locale]);
};

export default useBreadcrumbListSchema;
